"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

function ResumeParsingAnimation() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="relative flex flex-col items-center justify-center py-2">
      {/* Background ambient glow */}
      <motion.div
        animate={
          shouldReduceMotion
            ? undefined
            : {
                scale: [0.95, 1.1, 0.95],
                opacity: [0.35, 0.65, 0.35],
              }
        }
        transition={
          shouldReduceMotion
            ? undefined
            : { duration: 3, repeat: Infinity, ease: "easeInOut" }
        }
        className="absolute h-32 w-32 rounded-full bg-[#ED5AA9]/20 blur-xl pointer-events-none"
      />

      {/* Document Card */}
      <div className="relative flex h-36 w-28 flex-col justify-between overflow-hidden rounded-lg border border-black/15 bg-white p-2.5 shadow-md">
        {/* Document Content */}
        <div className="space-y-2.5">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-slate-300 shrink-0" />
            <div className="space-y-1 flex-1 min-w-0">
              <div className="h-2 w-14 rounded-full bg-slate-400" />
              <div className="h-1.5 w-9 rounded-full bg-slate-200" />
            </div>
          </div>

          <div className="h-[1px] w-full bg-slate-100" />

          {/* Section 1: Illuminated Text Lines */}
          <div className="space-y-1.5">
            <motion.div
              animate={
                shouldReduceMotion
                  ? undefined
                  : {
                      backgroundColor: [
                        "#e2e8f0",
                        "#ED5AA9",
                        "#e2e8f0",
                        "#e2e8f0",
                      ],
                    }
              }
              transition={
                shouldReduceMotion
                  ? undefined
                  : {
                      duration: 2.4,
                      repeat: Infinity,
                      times: [0, 0.25, 0.55, 1],
                    }
              }
              className="h-2 w-full rounded-full bg-slate-200"
            />
            <motion.div
              animate={
                shouldReduceMotion
                  ? undefined
                  : {
                      backgroundColor: [
                        "#e2e8f0",
                        "#e2e8f0",
                        "#ED5AA9",
                        "#e2e8f0",
                      ],
                    }
              }
              transition={
                shouldReduceMotion
                  ? undefined
                  : {
                      duration: 2.4,
                      repeat: Infinity,
                      times: [0, 0.45, 0.75, 1],
                    }
              }
              className="h-1.5 w-4/5 rounded-full bg-slate-200"
            />
            <div className="h-1.5 w-full rounded-full bg-slate-200" />
          </div>

          {/* Section 2: Animated Tag Badges */}
          <div className="pt-1 space-y-1.5">
            <div className="flex gap-1.5">
              <motion.div
                animate={
                  shouldReduceMotion
                    ? undefined
                    : {
                        backgroundColor: [
                          "#e2e8f0",
                          "#ED5AA9",
                          "#e2e8f0",
                        ],
                      }
                }
                transition={
                  shouldReduceMotion
                    ? undefined
                    : {
                        duration: 2.4,
                        repeat: Infinity,
                        times: [0, 0.5, 1],
                      }
                }
                className="h-2 w-10 rounded-full bg-slate-200"
              />

              <motion.div
                animate={
                  shouldReduceMotion
                    ? undefined
                    : {
                        backgroundColor: [
                          "#e2e8f0",
                          "#ED5AA9",
                          "#e2e8f0",
                        ],
                      }
                }
                transition={
                  shouldReduceMotion
                    ? undefined
                    : {
                        duration: 2.4,
                        repeat: Infinity,
                        times: [0.2, 0.7, 1],
                      }
                }
                className="h-2 w-12 rounded-full bg-slate-200"
              />
            </div>
            <div className="h-1.5 w-3/4 rounded-full bg-slate-200" />
          </div>
        </div>

        {/* Laser Scanning Line */}
        {!shouldReduceMotion && (
          <motion.div
            animate={{ top: ["0%", "100%", "0%"] }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute left-0 right-0 z-10 h-0.5 bg-[#ED5AA9] shadow-[0_0_12px_2px_#ED5AA9]"
          />
        )}
      </div>
    </div>
  );
}


export function ParsingStep({ restoring }: { restoring: boolean }) {
  const t = useTranslations("profile.resumePersonalization");

  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3 px-2 py-2 text-center">
      <ResumeParsingAnimation />

      <div className="space-y-1 max-w-xs">
        <p className="font-bold text-base text-[#101828]">
          {restoring ? t("parsing.loadingTitle") : t("parsing.readingTitle")}
        </p>
        <p className="text-xs text-[#667085]">
          {restoring
            ? t("parsing.loadingDescription")
            : t("parsing.readingDescription")}
        </p>
      </div>
    </div>
  );
}
